<template>
  <div class="card">
    <div class="card-header">
      <span class="text-header">Política de cancelamento</span>
    </div>
    <div class="card-body">
      <p class="text-paragraph">
        Sessões pagas via boleto não podem ser canceladas. No entanto, é
        possível realizar o reagendamento, se necessário.
      </p>

      <Button
        v-if="isCancelable"
        label="Cancelar esta sessão"
        fullWidth
        outline
        outlineColor="danger"
        @onClick="toggleModal(modalId)"
      />
    </div>
  </div>
  <ModalComponent :id="modalId" :title="modalTitle" backdrop="false">
    <template v-slot:body>
      <RadioInput
        :model="cancelAllModel"
        :options="cancelAllOptions"
        v-if="item.recurrency"
      />

      <TextInput
        required
        label="Motivo"
        :model="reasonForCancellationModel"
        placeholder="Por que deseja cancelar?"
      />
    </template>
    <template v-slot:footer>
      <Button
        outline
        outlineColor="primary"
        label="Não, voltar"
        @onClick="toggleModal(modalId)"
      />
      <FormButton
        label="Sim, cancelar esta sessão"
        color="danger"
        @onClick="handleCancelConfirmed"
        :loading="submitting"
        :disabled="!meta.valid"
      />
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { Appointment } from "@phurshell/psiapp-frontend-core/domain";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { useVeeField } from "@/composables/useVeeField";
import { cancellationSchema } from "@/schemas/appointment";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { toggleModal } from "@/utils/Helper";
import { syncSession } from "@/di/useCases";
import { useUpdateAppointment, useInvalidateAppointments } from "@/composables/useAppointments";
import { closeOffCanvas } from "@/views/OffcanvasViews/OffcanvasViewsConfig";

const props = defineProps<{ item: Appointment }>();

const modalId = "cancel_appointment_modal";
const submitting = ref(false);

const cancelAllOptions = [
  { id: false, title: "Somente esta sessão" },
  { id: true, title: "Esta sessão e as próximas" },
];

const { handleSubmit, meta } = useForm({
  validationSchema: toTypedSchema(cancellationSchema),
  initialValues: { reason_for_cancellation: "", cancel_all: false },
});

const reasonForCancellationModel = useVeeField("reason_for_cancellation");
const cancelAllModel = useVeeField("cancel_all");

const isCancelable = computed(
  () => props.item.status === "agendado" && !props.item.is_waiting_boleto_payment
);

const modalTitle = computed(() =>
  props.item.recurrency
    ? "Qual sessão você deseja cancelar?"
    : "Tem certeza que deseja cancelar esta sessão?"
);

const { mutateAsync: updateAppointment } = useUpdateAppointment();
const invalidateAppointments = useInvalidateAppointments();

const handleCancelConfirmed = handleSubmit(async (values) => {
  try {
    submitting.value = true;
    const params: any = {
      status: props.item.status === "agendado" ? "cancelado" : "recusado",
      reason_for_cancellation: values.reason_for_cancellation,
    };

    if (values.cancel_all) {
      params.cancel_recurrency = true;
    }

    await updateAppointment({ id: props.item.id, ...params });
    await syncSession.execute();

    if (props.item.status === "pendente") {
      closeOffCanvas();
      return;
    }

    toggleModal(modalId);
    EventBus.$emit(EventBusNames.UPDATE_APPOINTMENTS);
  } catch (e) {
    console.log(e);
  } finally {
    submitting.value = false;
  }
});
</script>
